import {
  Button as AntButton,
  Dropdown,
  Empty,
  Form,
  InputNumber,
  Switch,
  Table,
  Tag,
  Tooltip,
} from 'antd';
import {
  MoreOutlined,
  PlusOutlined,
  ReloadOutlined,
  SwapRightOutlined,
} from '@ant-design/icons';
import styles from './fxExchangeRate.module.scss';
// Page chrome shared with the swap transactions screen, so the two swap
// screens read as one product.
import shell from '../view/swap/swapTransactions.module.scss';
import {currencyName, formatRateValue} from 'utils/currencyFormatting';
import {useLazyQuery, useMutation, useQuery} from '@apollo/client';
import {
  GET_ALL_FX_EXCHANGE_RATES,
  GET_PUBLIC_EXCHANGE_RATES,
} from 'graphql/queries/wallet';
import {useNavigate} from 'react-router-dom';
import SubmitButton from 'components/Startup/components/SubmitButton';
import {currencyMatch} from 'pages/campaigns/campaignData';
import {
  DELETE_FX_EXCHANGE_PAIR,
  ENABLE_PAIR_STATUS,
  SET_DAILY_THRESHOLD_FOR_SWAP,
} from 'graphql/mutations/wallet';
import DBModal from 'components/modal/modal';
import {useEffect, useState} from 'react';
import {Button} from '@dabafinance/react-components';
import {toast} from 'react-toastify';
import {currencySymbols} from 'utils/mock';
import {NavTabsEnum} from 'utils/constants';
import {makeSelectTabOperations} from 'redux/store/auth';
import {useSelector} from 'react-redux';

const FxExchangeRate = () => {
  const [modalData, setModalData] = useState({});
  const {data, loading, refetch} = useQuery(GET_ALL_FX_EXCHANGE_RATES);
  const [fieldValues, setFieldValues] = useState({});
  const [isFinishScreen, setIsFinishScreen] = useState(false);

  const tabOperations = useSelector(state =>
    makeSelectTabOperations(state, NavTabsEnum.Swap),
  );

  const [marketRatesState, setMarketRatesState] = useState({});

  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [removeFxExchangeRate, {loading: loadingDelete}] = useMutation(
    DELETE_FX_EXCHANGE_PAIR,
  );
  const [enableOrDisableFxExchangeRate, {loading: loadingEnable}] =
    useMutation(ENABLE_PAIR_STATUS);

  const [setDailyThresholdForAutomaticSwap, {loading: loadingThresholdUpdate}] =
    useMutation(SET_DAILY_THRESHOLD_FOR_SWAP);
  const [checkMarketRate, {loading: loadingRate}] = useLazyQuery(
    GET_PUBLIC_EXCHANGE_RATES,
    {
      fetchPolicy: 'no-cache',
      onError: error => toast.error(error.message),
      errorPolicy: 'all',
    },
  );

  const handleCheckMarketRate = (sourceCurrency, targetCurrency) => {
    checkMarketRate({
      variables: {
        from: sourceCurrency,
        to: [targetCurrency],
      },
    }).then(({data}) => {
      if (
        data &&
        !loadingRate &&
        sourceCurrency === data?.getPublicFxExchangeRates?.from &&
        targetCurrency === data?.getPublicFxExchangeRates?.to[0]?.currency
      ) {
        setMarketRatesState(prevState => ({
          ...prevState,
          [`${sourceCurrency}_${targetCurrency}`]: {
            checked: true,
            value: data?.getPublicFxExchangeRates?.to[0]?.rate,
          },
        }));
      }
    });
  };

  useEffect(() => {
    if (modalData) {
      form.setFieldsValue({
        amount: modalData.amount,
        remain: modalData.remaining,
      });
    }
  }, [modalData]);

  const handleDeletePair = () => {
    removeFxExchangeRate({
      variables: {
        removeFxExchangeRateId: modalData.id,
      },
    })
      .then(({data: {removeFxExchangeRate}}) => {
        if (removeFxExchangeRate.__typename === 'Error') {
          toast.error(removeFxExchangeRate.message);
        } else {
          toast.success('Swap Pair Deleted Successfully');
          refetch();
          setModalData({isOpen: false});
        }
      })
      .catch(error => {
        toast.error(error);
      });
  };

  const handleUpdatePairStatus = () => {
    enableOrDisableFxExchangeRate({
      variables: {
        enableOrDisableFxExchangeRateId: modalData.id,
        isEnabled: !modalData.enabled,
      },
      refetchQueries: [{query: GET_ALL_FX_EXCHANGE_RATES}],
    })
      .then(({data: {enableOrDisableFxExchangeRate}}) => {
        if (enableOrDisableFxExchangeRate.__typename === 'Error') {
          toast.error(enableOrDisableFxExchangeRate.message);
        } else {
          toast.success('Swap Pair Updated Successfully');
          refetch();
          setModalData({isOpen: false});
        }
      })
      .catch(error => {
        toast.error(error);
      });
  };

  const columns = [
    {
      title: 'Pair',
      dataIndex: 'sourceCurrency',
      key: 'sourceCurrency',
      render: (text, record) => (
        // An arrow, not a slash: every rule on this row is directional, and
        // `XOF / USD` reads like a symmetrical pair when it is not.
        <div className={styles.pairCell}>
          <span className={styles.pairCode}>
            {record?.sourceCurrency} <SwapRightOutlined />{' '}
            {record?.targetCurrency}
          </span>
          <span className={styles.pairNames}>
            {currencyName(record?.sourceCurrency)} to{' '}
            {currencyName(record?.targetCurrency)}
          </span>
        </div>
      ),
    },
    {
      title: 'Rate',
      dataIndex: 'type',
      key: 'type',
      render: (text, record) => (
        <div className={styles.rateCell}>
          <span className={styles.rateValue}>
            {currencyMatch[record?.targetCurrency]?.symbol}
            {formatRateValue(record?.rate)}
          </span>
          <span className={styles.rateUnit}>
            per 1 {record?.sourceCurrency}
          </span>
        </div>
      ),
    },

    {
      title: 'Market Rate',
      dataIndex: 'marketRate',
      key: 'marketRate',
      render: (text, record) => {
        const {sourceCurrency, targetCurrency} = record;
        const checkerField = [`${sourceCurrency}_${targetCurrency}`];
        const isMarketRateChecked = marketRatesState[checkerField]?.checked;

        if (!isMarketRateChecked) {
          return (
            <AntButton
              size="small"
              loading={loadingRate}
              onClick={() =>
                handleCheckMarketRate(sourceCurrency, targetCurrency)
              }>
              Check
            </AntButton>
          );
        }

        // The reason to check a market rate is to compare it with the one
        // configured, so the comparison is shown rather than left as mental
        // arithmetic between two columns.
        const market = Number(marketRatesState[checkerField]?.value);
        const configured = Number(record?.rate);
        const drift =
          Number.isFinite(market) && Number.isFinite(configured) && configured
            ? ((configured - market) / market) * 100
            : null;

        return (
          <div className={styles.rateCell}>
            <span className={styles.rateValue}>
              {currencySymbols[targetCurrency]?.symbol}{' '}
              {formatRateValue(market)}
            </span>
            {drift !== null && (
              <Tooltip
                title={`The configured rate is ${
                  drift >= 0 ? 'above' : 'below'
                } the market rate by ${Math.abs(drift).toFixed(2)}%.`}>
                <span
                  className={
                    Math.abs(drift) >= 5 ? styles.driftWide : styles.driftOk
                  }>
                  {drift >= 0 ? '+' : ''}
                  {drift.toFixed(2)}% vs ours
                </span>
              </Tooltip>
            )}
          </div>
        );
      },
    },
    {
      title: 'Daily Threshold',
      dataIndex: 'dailyThreshold',
      key: 'dailyThreshold',
      render: (text, record) =>
        record?.dailyThresholdForAutomaticSwapToTargetCurrency?.initial ? (
          <span>
            {currencySymbols[record?.targetCurrency]?.symbol}{' '}
            {record?.dailyThresholdForAutomaticSwapToTargetCurrency?.initial?.toLocaleString()}
          </span>
        ) : (
          <Tag color="default">NOT SET</Tag>
        ),
    },
    {
      title: 'Remaining Daily Threshold',
      dataIndex: 'dailyThreshold',
      key: 'dailyThreshold',
      render: (text, record) =>
        record?.dailyThresholdForAutomaticSwapToTargetCurrency?.remaining ? (
          <span>
            {currencySymbols[record?.targetCurrency]?.symbol}{' '}
            {record?.dailyThresholdForAutomaticSwapToTargetCurrency?.remaining?.toLocaleString()}
          </span>
        ) : (
          <Tag color="default">NOT SET</Tag>
        ),
    },
    {
      // Who may use the pair, visible without opening each one. Both gates are
      // per direction, and every gate set on a pair has to pass.
      title: 'Restrictions',
      dataIndex: 'restrictions',
      key: 'restrictions',
      render: (text, record) => {
        const gates = [
          record?.requireDepositForTargetCurrencySwap && {
            key: 'deposit',
            label: `Deposit in ${record?.targetCurrency}`,
            title: `The user must have a successful deposit in ${record?.targetCurrency} to use this pair.`,
          },
          record?.requirePriorSwapFromTargetCurrency && {
            key: 'swap',
            label: `Prior ${record?.targetCurrency} → ${record?.sourceCurrency}`,
            title: `The user must have a successful ${record?.targetCurrency} → ${record?.sourceCurrency} swap to use this pair.`,
          },
        ].filter(Boolean);

        if (!gates.length) return <span>Open to all</span>;
        return gates.map(gate => (
          <Tag color="orange" key={gate.key} title={gate.title}>
            {gate.label}
          </Tag>
        ));
      },
    },
    {
      title: 'Enabled',
      dataIndex: 'enabled',
      key: 'enabled',
      render: (text, record) => (
        <div style={{maxWidth: '50px'}}>
          <Switch
            checked={record?.enabled}
            defaultChecked={record?.enabled}
            disabled={!tabOperations?.update}
            onClick={() =>
              setModalData({
                isOpen: true,
                enabled: record?.enabled,
                id: record.id,
                type: 'ENABLE',
              })
            }
          />
        </div>
      ),
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      width: 60,
      fixed: 'right',
      render: (text, record) => {
        // `menu`, not `overlay`: antd deprecated the latter and logged a
        // warning on every render of this page.
        const items = [
          tabOperations?.update && {
            key: 'update',
            label: 'Update settings',
            onClick: () =>
              navigate(`/dashboard/fx-exchange-rate/update/${record?.id}`),
          },
          tabOperations?.update && {
            key: 'transactions',
            label: 'View transactions',
            onClick: () =>
              navigate(
                `/dashboard/wallet/swap-transactions/${record?.sourceCurrency}`,
              ),
          },
          tabOperations?.update && {
            key: 'threshold',
            label: 'Set daily threshold',
            onClick: () =>
              setModalData({
                thresholdOpen: true,
                id: record.id,
                sourceCurrency: record?.sourceCurrency,
                targetCurrency: record?.targetCurrency,
                amount:
                  record?.dailyThresholdForAutomaticSwapToTargetCurrency
                    ?.initial,
                remaining:
                  record?.dailyThresholdForAutomaticSwapToTargetCurrency
                    ?.shouldNotifyAdminIfRemainingIsBelow,
                type: 'THRESHOLD',
              }),
          },
          // Deleting stays behind disabling, so a live pair cannot go in one
          // click.
          !record?.enabled &&
            tabOperations?.delete && {
              key: 'delete',
              danger: true,
              label: 'Delete pair',
              onClick: () => setModalData({isOpen: true, id: record.id}),
            },
        ].filter(Boolean);

        if (!items.length) return null;

        return (
          <Dropdown trigger={['click']} menu={{items}}>
            <AntButton
              type="text"
              icon={<MoreOutlined />}
              onClick={e => e.stopPropagation()}
            />
          </Dropdown>
        );
      },
    },
  ];

  const handleSetThreshold = () => {
    setDailyThresholdForAutomaticSwap({
      variables: {
        amount: Number(fieldValues.amount),
        sourceCurrency: modalData.sourceCurrency,
        targetCurrency: modalData.targetCurrency,
        ...(fieldValues.remain && {
          shouldNotifyAdminIfRemainingIsBelow: Number(fieldValues.remain),
        }),
      },
    })
      .then(({data: {setDailyThresholdForAutomaticSwap}}) => {
        if (setDailyThresholdForAutomaticSwap.__typename === 'Error') {
          toast.error(setDailyThresholdForAutomaticSwap.message);
        } else {
          toast.success('Swap Pair Daily Threshold Updated Successfully');
          setModalData({thresholdOpen: false});
          setIsFinishScreen(false);
          refetch();
        }
      })
      .catch(error => {
        toast.error(error);
      });
  };

  const rates = data?.getAllFxExchangeRates?.rates ?? [];
  const liveCount = rates.filter(rate => rate?.enabled).length;
  const gatedCount = rates.filter(
    rate =>
      rate?.requireDepositForTargetCurrencySwap ||
      rate?.requirePriorSwapFromTargetCurrency,
  ).length;

  return (
    <div className={shell.page}>
      <div className={shell.header}>
        <div className={shell.titleWrap}>
          <h1 className={shell.title}>Currency swap pairs</h1>
          <p className={shell.subtitle}>
            Every pair users can swap through, and the rate, limits and
            restrictions on each. Each direction is configured separately.
          </p>
        </div>
        <div className={shell.headerActions}>
          <AntButton
            icon={<ReloadOutlined />}
            onClick={() => refetch()}
            loading={loading}>
            Refresh
          </AntButton>
          <AntButton
            type="primary"
            icon={<PlusOutlined />}
            className={shell.primaryButton}
            disabled={!tabOperations?.create}
            title={
              tabOperations?.create
                ? undefined
                : 'You need create permission to add a pair'
            }
            onClick={() => navigate('/dashboard/fx-exchange-rate/create')}>
            Add swap pair
          </AntButton>
        </div>
      </div>

      {/* A count beats scanning the Enabled column, and the gated count is
          the one an operator is usually hunting for. */}
      <div className={shell.filterMeta}>
        <span className={shell.resultCount}>{rates.length} pairs</span>
        <Tag color="green">{liveCount} live</Tag>
        <Tag>{rates.length - liveCount} disabled</Tag>
        {gatedCount > 0 && <Tag color="orange">{gatedCount} restricted</Tag>}
      </div>

      <section className={`${shell.tableContainer} ${styles.scrollTable}`}>
        <Table
          rowKey="id"
          loading={loading}
          dataSource={rates}
          columns={columns}
          scroll={{x: 'max-content'}}
          rowClassName={record => (record?.enabled ? '' : styles.disabledRow)}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description="No swap pairs yet."
              />
            ),
          }}
        />
      </section>
      <DBModal
        isOpen={modalData.isOpen}
        handleClose={() => setModalData({isOpen: false})}
        content={
          <div className={styles['modal-container']}>
            {modalData.type === 'ENABLE' ? (
              <h1>Enable / Disable this Pair</h1>
            ) : (
              <h1>Are you sure You want to Delete This Currency Pair?</h1>
            )}
            <div>
              <Button
                disabled={loadingDelete || loadingEnable}
                className={styles['modal-button']}
                label={'Yes'}
                onClick={
                  modalData.type === 'ENABLE'
                    ? handleUpdatePairStatus
                    : handleDeletePair
                }
              />
              <Button
                className={styles['modal-button']}
                type="secondary"
                label="No"
                onClick={() => setModalData({isOpen: false})}
              />
            </div>
          </div>
        }
      />
      <DBModal
        isOpen={modalData.thresholdOpen}
        handleClose={() => setModalData({thresholdOpen: false})}
        content={
          isFinishScreen ? (
            <>
              <div className="finishScreen">
                <h1>You are about to UPDATE the Daily Threshold Limit</h1>
                <p>
                  Are you sure you want to Proceed with updating this on{' '}
                  {modalData?.sourceCurrency}/{modalData?.targetCurrency} Pair?
                </p>
                <div className="finish-items">
                  <div className="items">
                    <p>Threshold Amount</p>
                    <span>
                      {currencySymbols[modalData?.targetCurrency]?.symbol}
                      {fieldValues?.amount.toLocaleString()}
                    </span>
                  </div>
                  <div className="items">
                    <p>Amount to Notify Admin</p>
                    <span>
                      {currencySymbols[modalData?.targetCurrency]?.symbol}
                      {fieldValues?.remain.toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="finish-buttons">
                  <Button
                    className="finish-button"
                    label="No"
                    onClick={() => setIsFinishScreen(false)}
                    type="secondary"
                  />
                  <Button
                    onClick={handleSetThreshold}
                    className="finish-button"
                    disabled={loadingThresholdUpdate}
                    label="Yes"
                  />
                </div>
              </div>
            </>
          ) : (
            <div className={styles['modal-container']}>
              <h1>
                Update Daily Threshold Setting for{' '}
                {modalData.sourceCurrency + '/' + modalData.targetCurrency} Pair
              </h1>
              <Form
                onFinish={values => {
                  setFieldValues(values);
                  setIsFinishScreen(true);
                }}
                form={form}
                layout="vertical">
                <Form.Item
                  rules={[{required: true, message: 'This Field is required'}]}
                  name="amount"
                  label="Threshold Amount">
                  <InputNumber
                    className={styles.bigWidth}
                    formatter={value =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
                <Form.Item
                  name="remain"
                  label="Limit Amount (Once the Threshold Amount is below this Amount, the admin would be notified)">
                  <InputNumber
                    className={styles.bigWidth}
                    formatter={value =>
                      `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
                    }
                    parser={value => value.replace(/\$\s?|(,*)/g, '')}
                  />
                </Form.Item>
                <SubmitButton label="Update" />
              </Form>
            </div>
          )
        }
      />
    </div>
  );
};

export default FxExchangeRate;
